import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { serviceAPI } from '../lib/api';
import { KeyRound, Loader2, CheckCircle, ArrowRight, Clock, Shield, Repeat } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function RentDomainPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', domain_name: '', message: '', budget: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await serviceAPI.request({ ...form, service_type: 'rent_domain' });
      setSubmitted(true);
      toast.success('Rental request submitted!');
    } catch { toast.error('Failed to submit'); }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="px-6 md:px-12 max-w-7xl mx-auto py-20">
        <div className="max-w-lg mx-auto bg-[#0A0A0A] border border-white/5 rounded-sm p-12 text-center">
          <CheckCircle className="h-12 w-12 text-[#00FF94] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Request Submitted!</h3>
          <p className="text-zinc-500">We'll match you with available domains and respond within 24 hours.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="rent-domain-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <KeyRound className="h-10 w-10 text-[#00E5FF] mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">{t('rentDomain.title')}</h1>
          <p className="text-zinc-500 mt-3 max-w-xl mx-auto">{t('rentDomain.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {[
            { icon: Clock, title: 'Flexible Terms', desc: 'Rent domains for 1 month to 1 year. Perfect for campaigns and testing.' },
            { icon: Shield, title: 'Secure Setup', desc: 'DNS pointing and SSL included. Full control during rental period.' },
            { icon: Repeat, title: 'Option to Buy', desc: 'Like the domain? Apply your rental fees toward purchase price.' },
          ].map((item, i) => (
            <div key={i} className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm">
              <item.icon className="h-8 w-8 text-[#00E5FF] mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2 font-['Outfit']">{item.title}</h3>
              <p className="text-sm text-zinc-500">{item.desc}</p>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8 space-y-5" data-testid="rent-domain-form">
          <h3 className="text-lg font-semibold text-white font-['Outfit']">Request Domain Rental</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.name')}</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="rent-name" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.email')}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="rent-email" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Desired Domain</Label>
              <Input value={form.domain_name} onChange={(e) => setForm(p => ({ ...p, domain_name: e.target.value }))}
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" placeholder="Specific domain or describe needs" data-testid="rent-domain" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Monthly Budget ($)</Label>
              <Input value={form.budget} onChange={(e) => setForm(p => ({ ...p, budget: e.target.value }))}
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" placeholder="Your budget" data-testid="rent-budget" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Project Details</Label>
            <Textarea value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} rows={3}
              className="mt-1.5 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm resize-none" placeholder="Tell us about your project and rental needs" data-testid="rent-details" />
          </div>
          <Button type="submit" disabled={loading}
            className="h-11 px-8 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="rent-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit Request <ArrowRight className="ml-2 h-4 w-4" /></>}
          </Button>
        </form>
      </div>
    </div>
  );
}
